'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import Script from 'next/script'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowLeft, MapPin, Phone, Mail, Loader2 } from 'lucide-react'
import posthog from 'posthog-js'
import type { CompanyPartner } from '@/payload-types'
import {
  trackContactFormSubmitted,
  trackLeadConversionPageview,
  trackPhoneCallInitiated,
  trackEmailLinkClicked,
} from '../../lib/analytics'
import { REFERRAL_OPTIONS } from '../../(site)/consultation/ConsultationContext'
import CompanyAutocomplete from './CompanyAutoComplete'
import SelectDropdown from './SelectDropdown'

const CONTACT_THANK_YOU_PATH = '/contact/thank-you'
import { mediaFile } from '../../lib/media'
import { OFFICE_LOCATIONS, OFFICE_LOCATIONS_TAGLINE } from '../../lib/officeLocations'

const headquarters = OFFICE_LOCATIONS.find((office) => office.isHeadquarters)!

const contactInfo = [
  {
    icon: MapPin,
    label: headquarters.address!,
    href: headquarters.mapsUrl!,
  },
  {
    icon: Phone,
    label: '(888) 875-8110',
    href: 'tel:+18888758110',
  },
  {
    icon: Mail,
    label: 'info@serottelaw.com',
    href: 'mailto:info@serottelaw.com',
  },
]

/** Messages pre-filled when linking to `/contact?interest=<key>`. */
export const CONTACT_INTEREST_MESSAGES: Record<string, string> = {
  'client-portal-demo':
    "Hi, I'm interested in scheduling a demo of your client portal. Please reach out so we can find a time that works.",
}

interface ContactFormProps {
  companyPartners: CompanyPartner[]
}

export default function ContactForm({ companyPartners }: ContactFormProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    referralSource: '',
    referralSourceOther: '',
    referringContactName: '',
    referringCompanyName: '',
    referringCompanySfId: '',
    referralType: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const interest = new URLSearchParams(window.location.search).get('interest')
    const prefill = interest ? CONTACT_INTEREST_MESSAGES[interest] : undefined
    if (prefill) {
      setFormData((prev) => (prev.message ? prev : { ...prev, message: prefill }))
    }
  }, [])

  const showReferralDetails = formData.referralSource === 'Referral'
  const showOtherInput = formData.referralSource === 'Unknown'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Honeypot: bots fill hidden fields, so fake a success and skip submitting
    const form = new FormData(e.currentTarget as HTMLFormElement)
    const hpA = form.get('hp_field_a')
    const hpB = form.get('hp_field_b')
    if (hpA || hpB) {
      posthog.capture('contact_honeypot_tripped', {
        hp_field_a: !!hpA,
        hp_field_b: !!hpB,
        email: formData.email,
      })
      setSubmitted(true)
      return
    }

    if (!formData.referralSource) {
      setError('Please let us know how you heard about us.')
      return
    }

    setIsSubmitting(true)

    try {
      // Get reCAPTCHA token
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, { action: 'submit_contact' })
            .then(resolve)
            .catch(reject)
        })
      })

      const referralType =
        formData.referringCompanyName.trim() || formData.referringContactName.trim()
          ? 'Indirect Referral'
          : 'No Referral'

      // Submit to API with reCAPTCHA token and form data
      const res = await fetch('/api/submit-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          formData: {
            ...formData,
            referralType,
          },
        }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed')
      }

      setSubmitted(true)

      posthog.identify(formData.email, {
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        source: 'contact_form',
      })
      trackContactFormSubmitted({ inquiry_type: 'general' })

      // Route the URL to a dedicated thank-you page so the completion can be
      // measured as a lead-conversion pageview (keeps the success UI in place).
      window.history.replaceState(window.history.state, '', CONTACT_THANK_YOU_PATH)
      trackLeadConversionPageview({ path: CONTACT_THANK_YOU_PATH, title: 'Contact — Thank You' })
    } catch (err) {
      console.error('Error submitting form:', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleReferringCompanyChange = (value: string) => {
    setFormData((prev) => ({ ...prev, referringCompanyName: value }))
  }

  return (
    <div className="min-h-screen bg-background flex relative">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
        strategy="afterInteractive"
      />
      {/* Left Side - Hero Image */}
      <div className="hidden lg:block w-[40%] h-screen sticky top-0">
        <div className="relative w-full h-full">
          <Image
            src={mediaFile('hero-min.webp')}
            alt="Serotte Law Firm Office"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <h1 className="text-3xl font-medium mb-3">
              Get in <span className="italic font-serif">touch.</span>
            </h1>
            <p className="text-white/80">{OFFICE_LOCATIONS_TAGLINE}</p>
            <p className="text-white/60 text-sm mt-3">
              We&apos;re here to help with your immigration needs.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Contact Form */}
      <div className="min-h-screen flex flex-col pt-20 lg:pt-4 w-full lg:w-[60%] ">
        <div className="flex-1 px-6 md:px-12 lg:px-16 py-8 h-fit">
          {/* Back to home link - mobile */}
          <div className="lg:hidden mb-6">
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>

          {/* Page Content */}
          <div className="max-w-xl lg:ml-12 pt-4 lg:pt-24">
            {/* Header */}
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-medium text-gray-900 mb-3">Contact Us</h2>
              <p className="text-gray-600">
                Send us a message and we&apos;ll get back to you as soon as possible.
              </p>
              <p className="text-gray-500 text-sm mt-2">{OFFICE_LOCATIONS_TAGLINE}</p>
            </div>

            {submitted ? (
              /* Success State */
              <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600 mb-6">
                  Thank you for reaching out. Our team will be in touch shortly.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false)
                    setFormData({
                      firstName: '',
                      lastName: '',
                      email: '',
                      phone: '',
                      message: '',
                      referralSource: '',
                      referralSourceOther: '',
                      referringContactName: '',
                      referringCompanyName: '',
                      referringCompanySfId: '',
                      referralType: '',
                    })
                    window.history.replaceState(window.history.state, '', '/contact')
                  }}
                  className="text-brand-blue font-medium hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              /* Contact Form */
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot fields - hidden from users */}
                <div aria-hidden="true" className="absolute -left-[9999px]">
                  <input type="text" name="hp_field_a" tabIndex={-1} autoComplete="off" />
                  <input type="text" name="hp_field_b" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all bg-white"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-white px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1.5"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-white px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="referralSource"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    How did you hear about us? *
                  </label>
                  <SelectDropdown
                    id="referralSource"
                    options={REFERRAL_OPTIONS}
                    value={formData.referralSource}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, referralSource: value }))
                    }
                  />
                </div>

                <AnimatePresence initial={false}>
                  {showOtherInput && (
                    <motion.div
                      key="referral-other"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                      <label
                        htmlFor="referralSourceOther"
                        className="block text-sm font-medium text-gray-700 mb-1.5"
                      >
                        Please specify
                      </label>
                      <input
                        type="text"
                        id="referralSourceOther"
                        name="referralSourceOther"
                        value={formData.referralSourceOther}
                        onChange={handleChange}
                        placeholder="How did you hear about us?"
                        className="w-full bg-white px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all"
                      />
                    </motion.div>
                  )}

                  {showReferralDetails && (
                    <motion.div
                      key="referral-details"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label
                            htmlFor="referringContactName"
                            className="block text-sm font-medium text-gray-700 mb-1.5"
                          >
                            Name of Referring Contact
                          </label>
                          <input
                            type="text"
                            id="referringContactName"
                            name="referringContactName"
                            value={formData.referringContactName}
                            onChange={handleChange}
                            placeholder="John Doe"
                            className="w-full bg-white px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="referringCompanyName"
                            className="block text-sm font-medium text-gray-700 mb-1.5"
                          >
                            Name of Referring Company
                          </label>
                          <CompanyAutocomplete
                            id="referringCompanyName"
                            companyPartners={companyPartners}
                            value={formData.referringCompanyName}
                            onChange={handleReferringCompanyChange}
                            onSelect={(company) =>
                              setFormData((prev) => ({
                                ...prev,
                                referringCompanySfId: company?.sfId ?? '',
                                referringCompanyName: company?.name ?? prev.referringCompanyName,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1.5"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-white px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all resize-none"
                    placeholder="How can we help you?"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !formData.referralSource}
                  className="w-full bg-brand-blue text-white py-3.5 rounded-lg font-medium hover:bg-brand-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>

                {error && <p className="text-red-600 text-sm text-center">{error}</p>}
              </form>
            )}

            {/* Contact Info */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-x-8 gap-y-3">
                {contactInfo.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    onClick={() => {
                      if (item.href.startsWith('tel:')) trackPhoneCallInitiated()
                      if (item.href.startsWith('mailto:')) trackEmailLinkClicked()
                    }}
                    className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-blue transition-colors text-sm"
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
